import type { BudgetRule, BudgetRuleState } from "@adpilot/shared";
import { IconPencil, IconPlus, IconRefresh, IconStack2, IconTrash } from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { UnavailableControl } from "@/components/patterns/unavailable-control";
import { ErrorState } from "@/components/shared/error-state";
import {
  AlertDialog,
  AlertDialogClose,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogPopup,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Spinner } from "@/components/ui/spinner";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { getApiErrorMessage } from "@/lib/api-error";
import {
  detachBudgetRule,
  fetchCampaignBudgetRules,
  retryAttachBudgetRule,
  setBudgetRuleState,
} from "@/lib/budget-rules-api";
import { useFormatters } from "@/lib/format";
import { BudgetRuleDialog } from "./BudgetRuleDialog";

// Amazon's read-only evaluation status; anything else is shown verbatim.
const KNOWN_STATUSES = ["ACTIVE", "ARCHIVED", "EXPIRED", "PENDING"] as const;

interface CampaignBudgetRulesCardProps {
  campaignId: string;
  campaignType: string;
  archived: boolean;
  dailyBudget: number | null;
  currency: string;
  canWrite: boolean;
}

type Confirm =
  | { kind: "remove"; rule: BudgetRule }
  | { kind: "state"; rule: BudgetRule; state: BudgetRuleState };

/**
 * ADP-222: Amazon SP budget rules attached to this campaign — list, create,
 * edit, pause/resume and remove. Every change is written to Amazon first. A rule
 * is shop-level on Amazon, so changes to a shared rule are confirmed first.
 */
export function CampaignBudgetRulesCard({
  campaignId,
  campaignType,
  archived,
  dailyBudget,
  currency,
  canWrite,
}: CampaignBudgetRulesCardProps) {
  const { t } = useTranslation("campaign");
  const { formatCurrency, formatDate, formatDateTime, formatNumber } = useFormatters();
  const queryClient = useQueryClient();
  const supportedType = campaignType === "sp";
  const queryKey = ["campaign", campaignId, "budget-rules"];
  const query = useQuery({
    queryKey,
    queryFn: () => fetchCampaignBudgetRules(campaignId),
    enabled: supportedType,
  });
  const [editing, setEditing] = useState<BudgetRule | "new" | null>(null);
  const [confirm, setConfirm] = useState<Confirm | null>(null);

  const refresh = () => queryClient.invalidateQueries({ queryKey });
  const onError = async (error: unknown) => {
    void refresh();
    toast.error(await getApiErrorMessage(error));
  };
  const toggle = useMutation({
    mutationFn: ({ rule, state }: { rule: BudgetRule; state: BudgetRuleState }) =>
      setBudgetRuleState(campaignId, rule.id, state),
    onSuccess: () => {
      setConfirm(null);
      void refresh();
    },
    onError,
  });
  const remove = useMutation({
    mutationFn: (rule: BudgetRule) => detachBudgetRule(campaignId, rule.id),
    onSuccess: () => {
      toast.success(t("detail.budget.rules.removed"));
      setConfirm(null);
      void refresh();
    },
    onError,
  });
  const reattach = useMutation({
    mutationFn: (rule: BudgetRule) => retryAttachBudgetRule(campaignId, rule.id),
    onSuccess: () => {
      toast.success(t("detail.budget.rules.attached"));
      void refresh();
    },
    onError,
  });

  const writable = canWrite && supportedType && !archived;
  const lockedReason = !supportedType
    ? t("detail.budget.rules.unsupported")
    : archived
      ? t("detail.budget.rules.archived")
      : t("detail.budget.rules.readOnly");

  const requestState = (rule: BudgetRule) => {
    const state: BudgetRuleState = rule.state === "active" ? "paused" : "active";
    // A shared rule changes every campaign it is attached to — confirm first.
    if (rule.campaignCount > 1) setConfirm({ kind: "state", rule, state });
    else toggle.mutate({ rule, state });
  };

  const period = (rule: BudgetRule) => {
    const duration = rule.duration;
    if (duration.type === "event") return duration.eventName ?? duration.eventId;
    return duration.endDate
      ? `${formatDate(duration.startDate)} – ${formatDate(duration.endDate)}`
      : t("detail.budget.rules.fromDate", { date: formatDate(duration.startDate) });
  };
  const recurrence = (rule: BudgetRule) =>
    rule.recurrence.type === "daily"
      ? t("detail.budget.rules.recurrenceType.daily")
      : rule.recurrence.daysOfWeek.map((day) => t(`detail.budget.rules.day.${day}`)).join(" ");
  const status = (rule: BudgetRule) =>
    rule.status == null
      ? t("detail.budget.rules.status.pendingSync")
      : (KNOWN_STATUSES as readonly string[]).includes(rule.status)
        ? t(`detail.budget.rules.status.${rule.status}`)
        : rule.status;
  const percent = (value: number) => `+${formatNumber(value, { maximumFractionDigits: 2 })}%`;

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("detail.budget.rules.title")}</CardTitle>
        <CardDescription>{t("detail.budget.rules.description")}</CardDescription>
        <CardAction className="flex gap-2">
          <UnavailableControl variant="outline" reason={t("detail.budget.rules.bulkUnavailable")}>
            {t("detail.budget.rules.bulk")}
          </UnavailableControl>
          {writable ? (
            <Button size="sm" variant="outline" onClick={() => setEditing("new")}>
              <IconPlus data-icon="inline-start" />
              {t("detail.budget.advanced.addRule")}
            </Button>
          ) : (
            <UnavailableControl variant="outline" reason={lockedReason}>
              <IconPlus data-icon="inline-start" />
              {t("detail.budget.advanced.addRule")}
            </UnavailableControl>
          )}
        </CardAction>
      </CardHeader>
      <CardContent>
        {!supportedType ? (
          <p className="text-[12px] leading-4 text-muted-foreground">{lockedReason}</p>
        ) : query.isLoading ? (
          <Skeleton className="h-24 w-full" />
        ) : query.isError ? (
          <ErrorState
            variant="inline"
            message={t("detail.budget.rules.error")}
            onRetry={() => void query.refetch()}
            isRetrying={query.isFetching}
          />
        ) : (query.data?.rules.length ?? 0) === 0 ? (
          <p className="py-6 text-center text-[12px] leading-4 text-muted-foreground">
            {t("detail.budget.rules.empty")}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-14">{t("detail.budget.rules.col.switch")}</TableHead>
                  <TableHead>{t("detail.budget.rules.col.rule")}</TableHead>
                  <TableHead>{t("detail.budget.rules.col.status")}</TableHead>
                  <TableHead>{t("detail.budget.rules.col.type")}</TableHead>
                  <TableHead>{t("detail.budget.rules.col.dateRange")}</TableHead>
                  <TableHead>{t("detail.budget.rules.col.recurrence")}</TableHead>
                  <TableHead className="text-right">
                    {t("detail.budget.rules.col.increase")}
                  </TableHead>
                  <TableHead className="text-right">
                    {t("detail.budget.rules.col.budget")}
                  </TableHead>
                  <TableHead>{t("detail.budget.rules.col.createdAt")}</TableHead>
                  <TableHead className="text-right">
                    {t("detail.budget.rules.col.actions")}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {query.data?.rules.map((rule) => (
                  <TableRow key={rule.id}>
                    <TableCell>
                      <Switch
                        checked={rule.state === "active"}
                        disabled={!writable || !rule.attached || toggle.isPending}
                        aria-label={t("detail.budget.rules.toggleAria", { name: rule.name })}
                        onCheckedChange={() => requestState(rule)}
                      />
                    </TableCell>
                    <TableCell className="font-medium text-ink">
                      {rule.name}
                      {rule.performance ? (
                        <span className="block text-[11px] font-normal text-muted-foreground">
                          {t("detail.budget.rules.conditionSummary", {
                            metric: t(`detail.budget.rules.metric.${rule.performance.metric}`),
                            operator: t(
                              `detail.budget.rules.operator.${rule.performance.operator}`,
                            ),
                            threshold: formatNumber(rule.performance.threshold),
                          })}
                        </span>
                      ) : null}
                      <span className="mt-0.5 flex flex-wrap gap-1">
                        {!rule.attached ? (
                          <Badge variant="warning" size="compact">
                            {t("detail.budget.rules.notAttached")}
                          </Badge>
                        ) : null}
                        {rule.campaignCount > 1 ? (
                          <Badge variant="neutral" size="compact">
                            <IconStack2 aria-hidden />
                            {t("detail.budget.rules.shared", { count: rule.campaignCount })}
                          </Badge>
                        ) : null}
                        {!rule.editable ? (
                          <Badge variant="neutral" size="compact">
                            {t("detail.budget.rules.consoleOnly")}
                          </Badge>
                        ) : null}
                      </span>
                    </TableCell>
                    <TableCell className="text-muted-foreground">{status(rule)}</TableCell>
                    <TableCell>{t(`detail.budget.rules.ruleType.${rule.ruleType}`)}</TableCell>
                    <TableCell className="tabular-nums">{period(rule)}</TableCell>
                    <TableCell>{recurrence(rule)}</TableCell>
                    <TableCell className="text-right font-mono tabular-nums">
                      {percent(rule.increasePercent)}
                    </TableCell>
                    <TableCell className="text-right font-mono tabular-nums">
                      {dailyBudget != null
                        ? formatCurrency(dailyBudget * (1 + rule.increasePercent / 100), currency)
                        : "—"}
                    </TableCell>
                    <TableCell className="tabular-nums text-muted-foreground">
                      {rule.createdAt ? formatDateTime(rule.createdAt) : "—"}
                    </TableCell>
                    <TableCell>
                      {writable ? (
                        <div className="flex justify-end gap-1">
                          {!rule.attached ? (
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={reattach.isPending}
                              onClick={() => reattach.mutate(rule)}
                            >
                              {reattach.isPending ? (
                                <Spinner />
                              ) : (
                                <IconRefresh data-icon="inline-start" />
                              )}
                              {t("detail.budget.rules.retryAttach")}
                            </Button>
                          ) : null}
                          {rule.editable && rule.attached ? (
                            <Button
                              size="icon-sm"
                              variant="ghost"
                              aria-label={t("detail.budget.rules.editAria", { name: rule.name })}
                              onClick={() => setEditing(rule)}
                            >
                              <IconPencil />
                            </Button>
                          ) : null}
                          <Button
                            size="icon-sm"
                            variant="ghost"
                            aria-label={t("detail.budget.rules.removeAria", { name: rule.name })}
                            onClick={() => setConfirm({ kind: "remove", rule })}
                          >
                            <IconTrash />
                          </Button>
                        </div>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>

      <BudgetRuleDialog
        campaignId={campaignId}
        dailyBudget={dailyBudget}
        currency={currency}
        rule={editing && editing !== "new" ? editing : undefined}
        open={editing !== null}
        onOpenChange={(open) => !open && setEditing(null)}
      />

      <AlertDialog
        open={confirm !== null}
        onOpenChange={(open) => {
          if (!open && !remove.isPending && !toggle.isPending) setConfirm(null);
        }}
      >
        <AlertDialogPopup>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirm?.kind === "remove"
                ? t("detail.budget.rules.removeTitle")
                : t(
                    confirm?.state === "paused"
                      ? "detail.budget.rules.pauseSharedTitle"
                      : "detail.budget.rules.resumeSharedTitle",
                  )}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirm?.kind === "remove"
                ? t("detail.budget.rules.removeDescription", { name: confirm.rule.name })
                : t("detail.budget.rules.sharedDescription", {
                    name: confirm?.rule.name ?? "",
                    count: confirm?.rule.campaignCount ?? 0,
                  })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogClose
              render={<Button variant="outline">{t("common:actions.cancel")}</Button>}
            />
            <Button
              variant={confirm?.kind === "remove" ? "destructive" : "default"}
              disabled={remove.isPending || toggle.isPending}
              onClick={() => {
                if (confirm?.kind === "remove") remove.mutate(confirm.rule);
                else if (confirm) toggle.mutate({ rule: confirm.rule, state: confirm.state });
              }}
            >
              {remove.isPending || toggle.isPending ? <Spinner /> : null}
              {confirm?.kind === "remove"
                ? t("detail.budget.rules.removeConfirm")
                : t("common:actions.confirm")}
            </Button>
          </AlertDialogFooter>
        </AlertDialogPopup>
      </AlertDialog>
    </Card>
  );
}
